import { BLOCKED_TYPE_TEXT } from '@exam-center/blocked-user-management';
import { FormLeavingListener } from '@exam-center/components';
import {
  BlockedRecordBlockedScope,
  GetBlockedRecordByBlockedUserIdDocument,
  GetBlockedUserByIdDocument,
  useGetAllEnabledExamCoursesQuery,
  useUpdateBlockedRecordMutation,
} from '@exam-center/graphql';
import { useSnackbar } from '@shared/snackbar';
import { useEffect, useMemo, useState } from 'react';

import { BlockedRecordProctorForm } from '../../components';
import {
  BLOCKED_RECORD_PROCTOR_FIELDS_NAME,
  type BlockedRecordProctorFormInput,
  useBlockedRecordDetails,
  useBlockedRecordProctorFormContext,
} from '../../providers';

function markUndefinedIfEmptyString(value: string | null | undefined) {
  if (value === '') {
    return undefined;
  }
  return value;
}

export default function BlockedRecordProctorFormLogic() {
  const { showPromiseSnackbar } = useSnackbar();

  const { blockedUser, blockedRecord } = useBlockedRecordDetails();
  const [fieldVisibilityStatus, setFieldVisibilityStatus] = useState<{
    examCourses: boolean;
  }>({
    examCourses: blockedRecord.blockedScope === BlockedRecordBlockedScope.PartialScope,
  });

  const formContext = useBlockedRecordProctorFormContext();

  const { data: examCoursesData, loading: isLoadingCourses } = useGetAllEnabledExamCoursesQuery();

  const examCourseOptions = useMemo(() => {
    const examCourses = examCoursesData?.ecGetAllEnabledExamCourses.examCourses ?? [];

    return examCourses.map((course) => ({
      label: `${course.code}: ${course.name}`,
      value: course.code,
    }));
  }, [examCoursesData]);

  const [updateBlockedRecord, { loading: isUpdating }] = useUpdateBlockedRecordMutation({
    refetchQueries: [
      GetBlockedRecordByBlockedUserIdDocument,
      {
        query: GetBlockedUserByIdDocument,
        variables: {
          input: {
            id: blockedUser.id,
          },
        },
      },
    ],
  });

  const isSubmitting = isUpdating;

  const handleUpdateBlockedRecord = async (data: BlockedRecordProctorFormInput) => {
    const examCourseCodes =
      data.blockedCourses.type === BlockedRecordBlockedScope.PartialScope
        ? data.blockedCourses.courses?.map((course) => course.value)
        : undefined;

    const isAllCoursesBlocked = data.blockedCourses.type === BlockedRecordBlockedScope.AllScope;
    if (isAllCoursesBlocked) {
      formContext.resetField('blockedCourses.courses', { defaultValue: [] });
    }

    return await updateBlockedRecord({
      variables: {
        input: {
          blockedRecordId: blockedRecord.id,
          info: {
            blockedScope: data.blockedCourses.type,
            examCourseCodes,
            blockedReason: markUndefinedIfEmptyString(data.reason),
          },
        },
      },
    });
  };

  const handleSubmit = async (data: BlockedRecordProctorFormInput) => {
    showPromiseSnackbar({
      message: {
        pending: 'กำลังบันทึกข้อมูล',
        success: 'บันทึกข้อมูลเรียบร้อยแล้ว',
        error: 'บันทึกข้อมูลไม่สำเร็จ',
      },
      promise: () => handleUpdateBlockedRecord(data),
    });
  };

  useEffect(() => {
    // REMARK: handle visibility
    const subscription = formContext.watch((value, { name }) => {
      if (name === BLOCKED_RECORD_PROCTOR_FIELDS_NAME.blockedCourseType) {
        setFieldVisibilityStatus((prev) => ({
          ...prev,
          examCourses: value.blockedCourses?.type === BlockedRecordBlockedScope.PartialScope,
        }));
      }
    });

    return () => subscription.unsubscribe();
  }, [formContext]);

  useEffect(() => {
    if (formContext.formState.isSubmitSuccessful) {
      formContext.reset(formContext.getValues());
    }
  }, [formContext]);

  return (
    <>
      <BlockedRecordProctorForm
        onSubmit={formContext.handleSubmit(handleSubmit)}
        submitting={isSubmitting}
        fields={{
          idNumber: blockedUser.idNumber,
          title: blockedUser.title,
          firstName: blockedUser.firstName,
          middleName: blockedUser.middleName,
          lastName: blockedUser.lastName,
          email: blockedRecord.email || '-',
          phoneNumber: blockedUser.phoneNumber,
          blockedType: BLOCKED_TYPE_TEXT[blockedRecord.blockedType],
          position: blockedRecord.metadata.position || '-',
          department: blockedRecord.metadata.department || '-',
          blockedAt: blockedRecord.blockedAt,
          blockedUntil: blockedRecord.blockedUntil,
          enforcementDate: blockedRecord.metadata.enforcementEndDate,
          blockedCourse: {
            type: {
              name: BLOCKED_RECORD_PROCTOR_FIELDS_NAME.blockedCourseType,
            },
            courses: {
              name: BLOCKED_RECORD_PROCTOR_FIELDS_NAME.blockedCourseCourses,
              options: examCourseOptions,
              required: true,
              hidden: !fieldVisibilityStatus.examCourses,
              loading: isLoadingCourses,
            },
          },
          reason: {
            name: BLOCKED_RECORD_PROCTOR_FIELDS_NAME.reason,
          },
        }}
      />
      <FormLeavingListener onSubmit={handleSubmit} />
    </>
  );
}
